/**
 * Куда ведёт переключатель языка с текущей страницы.
 *
 * Обычно — на тот же путь на другом языке, и для статических страниц это
 * верно: они переведены всегда. Статьи блога — нет. Статья пишется под
 * рынок своего языка (BLOG_CONTENT_PLAN §3), и кнопка «SR» на русской
 * статье уводила читателя на 404.
 *
 * Дефект тихий вдвойне. Человек видит рабочую с виду кнопку и получает
 * ошибку, а поисковик находит в подвале каждой страницы ссылку на
 * несуществующий адрес — на всём сайте, а не только в блоге, потому что
 * подвал общий.
 *
 * Поэтому со статьи без перевода уводим в список статей на нужном языке:
 * там читатель увидит то, что для него действительно написано.
 */
import { POSTS, klasterStati, type PostLocale } from './posts';
import { AIS_RU_SITE_URL } from './ais-ru-domen';

export function localeSwitchPath(pathname: string, locale: string): string {
  const match = /^\/blog\/([^/]+)$/.exec(pathname);
  if (!match) return pathname;

  // Поиск по всему реестру, без оглядки на продукт: адрес статьи один на
  // платформу, а slug уникален глобально (это отдельно проверяет тест).
  // Фильтровать здесь по домену было бы нечем — функция работает в
  // браузере, где хоста запроса уже нет.
  const post = POSTS.find((p) => p.slug === match[1]);
  if (!post) return pathname;

  return post.locales.includes(locale as never) ? pathname : '/blog';
}

/**
 * Куда ведёт кнопка языка: путь на этом же сайте (ссылка next-intl с
 * `locale`) или готовый адрес другого сайта.
 */
export type LocaleSwitchTarget = { put: string } | { adres: string };

/**
 * Страницы программ на aisearch.tech: на каких языках они есть и какая
 * страница aisearch.ru им соответствует.
 *
 * Списки языков повторяют `generateStaticParams` этих страниц, а не
 * импортируют его: реестры текстов весят десятки килобайт и уехали бы в
 * JS каждой страницы вместе с подвалом. Что списки не разошлись, следит
 * тест `locale-switch.test.ts`.
 *
 * Русские пары те же, что в кластерах hreflang (`PARY_PROGRAMM` в
 * `ais-klaster.ts`; тот модуль тянет реестр инструментов, поэтому здесь
 * копия, а совпадение проверяет тест).
 */
const KONSPEKT_JAZYKI = ['en', 'es', 'sr'] as const;
export const AIS_TECH_PROGRAMMY: Record<string, { jazyki: readonly string[]; ru: string }> = {
  '/programs': { jazyki: KONSPEKT_JAZYKI, ru: '/programmy' },
  '/programs/konspekt': { jazyki: KONSPEKT_JAZYKI, ru: '/programmy/konspekt' },
  '/programs/konspekt/how-it-works': {
    jazyki: KONSPEKT_JAZYKI,
    ru: '/programmy/konspekt/kak-rabotaet',
  },
  '/programs/dictation': { jazyki: ['en', 'es'], ru: '/programmy/golosovoy-vvod' },
};

/**
 * Переключатель языка в подвале aisearch.tech.
 *
 * Русский ведёт на aisearch.ru, а не на aisearch.tech/ru: витрина .tech
 * на русском закрыта от индексации как дубль .ru, и hreflang ru уже
 * указывает на aisearch.ru (решение владельца 04.10.2026, `ais-seo.ts`).
 * Путь тот же — страницы обеих витрин лежат по одним адресам, кроме
 * программ, у которых русские адреса свои.
 *
 * Страница программы, не переведённая на выбранный язык (диктовка есть
 * только на en и es), уводит в список программ этого языка, а не на 404.
 */
export function aisTechLocaleSwitch(pathname: string, locale: string): LocaleSwitchTarget {
  const statya = statyaTech(pathname);
  if (statya) return statya(locale);

  const programma = AIS_TECH_PROGRAMMY[pathname];

  if (locale === 'ru') {
    const put = programma?.ru ?? pathname;
    return { adres: put === '/' ? AIS_RU_SITE_URL : `${AIS_RU_SITE_URL}${put}` };
  }

  if (programma && !programma.jazyki.includes(locale)) return { put: '/programs' };
  return { put: pathname };
}

/**
 * Статья блога aisearch.tech: переключатель ведёт на версию статьи на
 * выбранном языке — у русской свой slug и свой домен (aisearch.ru). Нет
 * перевода — в список статей этого языка, а не на 404.
 */
function statyaTech(pathname: string): ((locale: string) => LocaleSwitchTarget) | null {
  const match = /^\/blog\/([^/]+)$/.exec(pathname);
  const post = match
    ? POSTS.find((p) => p.product === 'aisearch' && p.slug === match[1])
    : undefined;
  if (!post) return null;
  const versii = klasterStati(post);
  return (locale) => {
    const slug = versii[locale as PostLocale];
    if (locale === 'ru') {
      return { adres: `${AIS_RU_SITE_URL}/blog${slug ? `/${slug}` : ''}` };
    }
    return { put: slug ? `/blog/${slug}` : '/blog' };
  };
}

/**
 * Переключатель языка в подвале ingdg.com.
 *
 * Русская версия раздела программ, покупки и условий лицензии есть на
 * самом ingdg.com (05.10.2026, владелец: русскоязычные живут не только в
 * России), поэтому «RU» остаётся на ingdg, а не уводит на aisearch.ru.
 * Диктовки на испанском нет (делового es-спроса нет, ТЗ Seo-marketing) —
 * с неё испанский ведёт в раздел программ.
 */
export function ingdgLocaleSwitch(pathname: string, locale: string): LocaleSwitchTarget {
  if (pathname === '/tools/dictation' && locale === 'es') return { put: '/software' };
  return { put: localeSwitchPath(pathname, locale) };
}
